import { h, render } from "preact";

import type {
  ConversationMessage,
  FeedbackDraft,
  FeedbackReason,
  FeedbackSnapshot,
} from "@go-novus/admin-api";

import { formatPrice } from "./conversation/reply-attachments";
import { resolveMessage, type WidgetChatMessage } from "./ui/message";
import { MESSAGE_MARKDOWN_STYLES } from "./ui/message-markdown";
import { MESSAGE_STYLES } from "./ui/message-parts";
import type { MessageTheme } from "./ui/theme";
import { MessageActions, MESSAGE_ACTION_STYLES } from "./ui/message-actions";
import { WidgetMessage } from "./ui/widget-message";
import { CHARACTER_STYLES } from "./ui/widget-character";

/** 买家看得到的两种消息；SYS 事件不进入商家结果回放。 */
type ReplayedMessage = ConversationMessage;

/** 没有正文、只有商品或业务卡的 AI 回复也要能赞踩。 */
export function assistantHasFeedback(message: ConversationMessage): boolean {
  return (
    message.role === "assistant" &&
    (message.text.trim() !== "" ||
      message.products.length > 0 ||
      message.blocks.length > 0 ||
      message.transferReason !== null)
  );
}

export interface ReplayOptions {
  /** 商品价格按哪种语言排：回放拿不到买家当时的店面语言，由后台定。 */
  locale: string;
  /** 颜色和圆角。后台传自己的 CSS 变量，变量穿得进 Shadow DOM，深浅色跟着后台切。 */
  theme: MessageTheme;
  /** 同一发言人连续消息只有组首缩角；独立挂载默认视为组首。 */
  groupStart?: boolean;
  feedback?: {
    edge: "start" | "end";
    snapshot?: FeedbackSnapshot;
    onLoadReasons: () => Promise<FeedbackReason[]>;
    onSubmit: (draft: FeedbackDraft) => Promise<FeedbackSnapshot>;
    onLikeError?: (message: string) => void;
    /** 已格式化的发送时刻，排在赞踩后面。 */
    sentAt?: { label: string; value: string };
  };
  /** 商家宿主按 workspace 时区格式化；未取得时区时不展示时间。 */
  formatDateTime?: (iso: string) => string;
}

function toChatMessage(
  message: ReplayedMessage,
  locale: string,
): WidgetChatMessage {
  if (message.role === "buyer") {
    return {
      id: message.id,
      role: "user",
      text: message.text,
      textFormat: "blocks",
    };
  }

  return {
    id: message.id,
    role: "ai",
    text: message.text,
    replayBlocks: message.blocks,
    products: message.products.map((product) => ({
      imageUrl: product.imageUrl ?? undefined,
      itemId: product.id,
      parentItemId: product.parentItemId ?? undefined,
      price: formatPrice(
        product.price?.amount,
        product.price?.currency,
        locale,
      ),
      title: product.title,
      url: product.url,
      variantId: product.variantId ?? undefined,
    })),
    // 店面只摆一行来源，就是后端的 grounding.label；BFF 也只拼得出这一条。
    source: message.sources[0]?.label,
  };
}

/**
 * 把一条买家或 AI 消息挂到后台 Inbox 回放里，返回卸载函数。画它的就是店面挂件那一份
 * 消息组件；Markdown、消息块顺序、商品卡和求助卡的展示规则都不在后台另写。
 * 样式隔在 Shadow DOM 里，后台的 Tailwind 进不来；字体和字号能继承进来，由宿主决定。
 */
export function mountReplayMessage(
  container: HTMLElement,
  message: ReplayedMessage,
  { locale, theme, groupStart = true, feedback, formatDateTime }: ReplayOptions,
): () => void {
  const shadowRoot =
    container.shadowRoot ?? container.attachShadow({ mode: "open" });
  const resolved = resolveMessage(toChatMessage(message, locale), theme);
  const buyer = message.role === "buyer";

  render(
    h(
      "div",
      {
        style: {
          "--gn-action": theme.faint,
          "--gn-action-accent": theme.accentFill,
          "--gn-action-accent-fg": theme.accentFillFg,
          color: theme.ink,
          lineHeight: 1.45,
        },
      },
      h("style", null, MESSAGE_MARKDOWN_STYLES),
      h("style", null, MESSAGE_STYLES),
      h("style", null, MESSAGE_ACTION_STYLES),
      h("style", null, CHARACTER_STYLES),
      h(WidgetMessage, {
        groupStart,
        handedOff:
          message.role === "assistant" && message.transferReason !== null,
        linksInNewTab: true,
        m: {
          ...resolved,
          align: buyer ? "flex-start" : "flex-end",
          background: buyer ? theme.bubbleBg : theme.accentFill,
          color: buyer ? theme.ink : theme.accentFillFg,
        },
        formatCardDateTime: (iso) => formatDateTime?.(iso) ?? "",
        feedbackEdge: feedback?.edge,
        actions:
          feedback && assistantHasFeedback(message)
            ? h(MessageActions, {
                delay: "0ms",
                edge: feedback.edge,
                snapshot: feedback.snapshot,
                sentAt: feedback.sentAt,
                onLoadReasons: feedback.onLoadReasons,
                onSubmit: feedback.onSubmit,
                onLikeError: feedback.onLikeError,
              })
            : undefined,
      }),
    ),
    shadowRoot,
  );

  return () => {
    render(null, shadowRoot);
  };
}
